import {
  Component,
  Input,
  forwardRef,
  Output,
  EventEmitter,
  OnDestroy,
  OnInit
} from '@angular/core';
import { FormControl, NG_VALUE_ACCESSOR, ControlValueAccessor } from '@angular/forms';
import { startWith, map, tap, debounceTime } from 'rxjs/operators';
import { Observable, of } from 'rxjs';
import { MatAutocompleteSelectedEvent } from '@angular/material/autocomplete';
import * as lodash from 'lodash';

import { AutoUnsubscribe, takeWhileAlive } from '@tripointe/tripointe-cms/utils';
import { DropdownItemValue } from '@tripointe/tripointe-cms/types';

interface AutoCompleteItem {
  text: string;
  value: string;
}

const AUTOCOMPLETE_VALUE_ACCESSOR = {
  provide: NG_VALUE_ACCESSOR,
  useExisting: forwardRef(() => AutocompleteComponent),
  multi: true
};

@Component({
  selector: 'tp-autocomplete',
  templateUrl: './autocomplete.component.html',
  styleUrls: ['./autocomplete.component.scss'],
  providers: [AUTOCOMPLETE_VALUE_ACCESSOR]
})
@AutoUnsubscribe()
export class AutocompleteComponent implements ControlValueAccessor, OnInit, OnDestroy {
  @Input() options: AutoCompleteItem[] = [];
  @Input() asyncDataLoading: boolean;
  @Input() set asyncFilteredOption(value: AutoCompleteItem[]) {
    this._asyncFilteredOption = lodash.uniqBy(value, ({ text }) => text);
    this.appointInitialValueToControl();
    this.filteredOptions$ = of(this._asyncFilteredOption);
  }
  @Input() placeholder = '';
  @Input() icon = 'search';
  @Output() queryChanged = new EventEmitter<string>();
  @Output() emitValue = new EventEmitter<string>();
  @Output() valueChange = new EventEmitter<DropdownItemValue>();

  selectedValue = '';
  ctrl = new FormControl();
  filteredOptions$: Observable<AutoCompleteItem[]>;
  _asyncFilteredOption: AutoCompleteItem[];
  propagateChange = (_: any) => {};
  onTouched = () => {};

  constructor() {
    this.filteredOptions$ = this.ctrl.valueChanges.pipe(
      startWith(''),
      tap(value => this.queryChanged.emit(value)),
      map(this.filterOptions)
    );
  }

  ngOnInit(): void {
    if (this.asyncDataLoading) this.subscribeToInput();
  }

  ngOnDestroy(): void {}

  private subscribeToInput(): void {
    this.ctrl.valueChanges
      .pipe(
        debounceTime(400),
        tap(value => this.queryChanged.emit(value)),
        takeWhileAlive(this)
      )
      .subscribe((value: string) => {
        this.emitValue.emit(value);
      });
  }

  private appointInitialValueToControl(): void {
    const activeOption: AutoCompleteItem = this._asyncFilteredOption.find(
      (option: AutoCompleteItem) => option.value === this.selectedValue
    );
    if (activeOption && !this.ctrl.value) {
      this.ctrl.setValue(activeOption.text);
    }
  }

  get currentOptions(): AutoCompleteItem[] {
    return this._asyncFilteredOption || this.options || [];
  }

  writeValue(value: string = ''): void {
    const selected = this.currentOptions.find(option => option.value === value);
    this.selectedValue = value;
    if (!selected && this.asyncDataLoading) this.emitValue.emit(value);

    this.ctrl.setValue(selected ? selected.text : value);
  }

  registerOnChange(fn: any): void {
    this.propagateChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  filterOptions = (searchTerm: string): any => {
    const query = this.getQuery(searchTerm);

    return this.currentOptions.filter(option => {
      return option.text
        .toLowerCase()
        .includes(typeof query === 'string' ? query.toLowerCase() : query);
    });
  };

  onOptionSelected(selectedEvent: MatAutocompleteSelectedEvent): void {
    const selectedOption: AutoCompleteItem = selectedEvent.option.value;
    this.selectedValue = selectedOption.value;
    this.ctrl.setValue(selectedOption.text);
    this.propagateChange(this.selectedValue);
    this.valueChange.emit(this.selectedValue);
  }

  onBlur(event: any): void {
    const query = this.getQuery(this.ctrl.value);
    const selected = this.currentOptions.find(option => option.text === query);
    const hasSameValue = selected && selected.value === this.selectedValue;
    const isSelectOptionEvent = event.relatedTarget && event.relatedTarget.tagName === 'MAT-OPTION';

    if (isSelectOptionEvent || hasSameValue) {
      return;
    }

    if (selected) {
      this.selectedValue = selected.value;
      this.propagateChange(this.selectedValue);
    } else {
      this.selectedValue = null;
      this.propagateChange(null);
      this.onTouched();
    }
  }

  getQuery(value: string | null): string {
    if (typeof value === 'string') {
      return value.trim();
    }

    return value;
  }
}
